// Shared by Cloud Functions and the frontend (imported there as @shared).
// Team season stats: running totals for the team and its opponents, and
// the categories the Leaders page ranks teams by.
//
// stats: {gp, pts, fgm, ..., pf, opp: {pts, fgm, ...}}

export const TEAM_STAT_KEYS = [
  "pts", "fgm", "fga", "tpm", "tpa", "ftm", "fta", "oreb", "dreb",
  "ast", "stl", "blk", "tov", "pf",
];

/**
 * @return {object} a team's stats before any games
 */
export function emptyTeamStats() {
  const zero = () => Object.fromEntries(TEAM_STAT_KEYS.map((k) => [k, 0]));
  return {gp: 0, ...zero(), opp: zero()};
}

/**
 * Adds one game. Returns new stats; the input is not changed.
 * @param {object} stats
 * @param {object} own this team's game totals (engine `totals`)
 * @param {object} opp the opponent's game totals
 * @return {object}
 */
export function addTeamGame(stats, own, opp) {
  const base = emptyTeamStats();
  const s = {...base, ...stats, opp: {...base.opp, ...stats?.opp}};
  s.gp += 1;
  for (const k of TEAM_STAT_KEYS) {
    s[k] += own?.[k] ?? 0;
    s.opp[k] += opp?.[k] ?? 0;
  }
  return s;
}

/**
 * Estimated possessions (the usual box-score formula).
 * @param {object} t totals
 * @return {number}
 */
export function possessions(t) {
  return t.fga - t.oreb + t.tov + 0.44 * t.fta;
}

const per = (s, x) => (s.gp ? x / s.gp : 0);
const pct = (made, att) => (att ? made / att : 0);
const rating = (pts, t) => {
  const poss = possessions(t);
  return poss ? 100 * pts / poss : 0;
};

// format: "1" one decimal, "pct" percentage, "signed" +/- one decimal,
// "wpct" a win percentage (".750"). low: lower is better (points allowed,
// turnovers, ...). Win % reads `w` and `l`, which the totals don't keep:
// callers copy them onto the stats from the team's record (withRecords).
export const TEAM_CATEGORIES = [
  {key: "winPct", label: "Win %", short: "WIN%", format: "wpct",
    value: (s) => pct(s.w ?? 0, (s.w ?? 0) + (s.l ?? 0))},
  {key: "ppg", label: "Points", short: "PPG", format: "1",
    value: (s) => per(s, s.pts)},
  {key: "oppg", label: "Points allowed", short: "OPP PPG", format: "1",
    low: true, value: (s) => per(s, s.opp.pts)},
  {key: "margin", label: "Scoring margin", short: "MARGIN", format: "signed",
    value: (s) => per(s, s.pts - s.opp.pts)},
  {key: "offRtg", label: "Offensive rating", short: "ORTG", format: "1",
    value: (s) => rating(s.pts, s)},
  {key: "defRtg", label: "Defensive rating", short: "DRTG", format: "1",
    low: true, value: (s) => rating(s.opp.pts, s.opp)},
  {key: "netRtg", label: "Net rating", short: "NET", format: "signed",
    value: (s) => rating(s.pts, s) - rating(s.opp.pts, s.opp)},
  {key: "pace", label: "Pace", short: "POSS", format: "1",
    value: (s) => per(s, (possessions(s) + possessions(s.opp)) / 2)},
  {key: "fgPct", label: "Field goal %", short: "FG%", format: "pct",
    value: (s) => pct(s.fgm, s.fga)},
  {key: "oppFgPct", label: "Opponent FG %", short: "OPP FG%", format: "pct",
    low: true, value: (s) => pct(s.opp.fgm, s.opp.fga)},
  {key: "tpPct", label: "3-point %", short: "3P%", format: "pct",
    value: (s) => pct(s.tpm, s.tpa)},
  {key: "tpm", label: "3-pointers made", short: "3PM", format: "1",
    value: (s) => per(s, s.tpm)},
  {key: "ftPct", label: "Free throw %", short: "FT%", format: "pct",
    value: (s) => pct(s.ftm, s.fta)},
  {key: "rpg", label: "Rebounds", short: "RPG", format: "1",
    value: (s) => per(s, s.oreb + s.dreb)},
  {key: "rebMargin", label: "Rebound margin", short: "REB +/-",
    format: "signed",
    value: (s) => per(s, s.oreb + s.dreb - s.opp.oreb - s.opp.dreb)},
  {key: "apg", label: "Assists", short: "APG", format: "1",
    value: (s) => per(s, s.ast)},
  {key: "astTo", label: "Assist-to-turnover", short: "AST/TO", format: "2",
    value: (s) => (s.tov ? s.ast / s.tov : 0)},
  {key: "topg", label: "Turnovers", short: "TOPG", format: "1", low: true,
    value: (s) => per(s, s.tov)},
  {key: "toMargin", label: "Turnover margin", short: "TO +/-",
    format: "signed", value: (s) => per(s, s.opp.tov - s.tov)},
  {key: "spg", label: "Steals", short: "SPG", format: "1",
    value: (s) => per(s, s.stl)},
  {key: "bpg", label: "Blocks", short: "BPG", format: "1",
    value: (s) => per(s, s.blk)},
];

/**
 * @param {object} cat a TEAM_CATEGORIES entry
 * @param {number} value
 * @return {string}
 */
export function formatTeamValue(cat, value) {
  if (cat.format === "pct") return `${(value * 100).toFixed(1)}%`;
  if (cat.format === "wpct") {
    return value >= 1 ? "1.000" : value.toFixed(3).replace(/^0/, "");
  }
  if (cat.format === "signed") {
    return `${value > 0 ? "+" : ""}${value.toFixed(1)}`;
  }
  return value.toFixed(cat.format === "2" ? 2 : 1);
}

/**
 * Copies each team's wins and losses onto its stats, for Win %.
 * @param {object} statsByTeam {teamId: stats}
 * @param {object} teams {teamId: {record}}
 * @param {boolean} [playoffs] use the playoff record (postW/postL)
 * @return {object} {teamId: stats with w and l}
 */
export function withRecords(statsByTeam, teams, playoffs = false) {
  const entries = Object.entries(statsByTeam ?? {});
  return Object.fromEntries(entries.map(([id, s]) => {
    const r = teams?.[id]?.record ?? {};
    return [id, {...s, w: (playoffs ? r.postW : r.w) ?? 0,
      l: (playoffs ? r.postL : r.l) ?? 0}];
  }));
}

/**
 * Ranks teams in one category, best first. Teams without a game are left
 * out.
 * @param {object} cat a TEAM_CATEGORIES entry
 * @param {object} statsByTeam {teamId: stats}
 * @return {object[]} [{teamId, value, rank, gp}]
 */
export function rankTeams(cat, statsByTeam) {
  const rows = Object.entries(statsByTeam ?? {})
      .filter(([, s]) => s?.gp > 0)
      .map(([teamId, s]) => ({teamId, value: cat.value(s), gp: s.gp}));
  rows.sort((a, b) => (cat.low ? a.value - b.value : b.value - a.value) ||
    a.teamId.localeCompare(b.teamId));
  rows.forEach((r, i) => {
    r.rank = i > 0 && r.value === rows[i - 1].value ? rows[i - 1].rank : i + 1;
  });
  return rows;
}
